import { z } from "zod";
import type {
    BanRule,
    BanRuleMatchMode,
    BannedEmail,
    BannedIp,
    CreateBanRuleRequest,
    CreateBannedEmailRequest,
    CreateBannedIpRequest,
    UpdateBanRuleRequest,
} from "@shopexperts/legacy-api";

/** Mirrors `BanRule::MATCH_MODES`, a fixed model constant. */
export const MATCH_MODE_OPTIONS: readonly { label: string; value: BanRuleMatchMode }[] = [
    { label: "Whole word", value: "word" },
    { label: "Substring", value: "substring" },
];

/**
 * The valid set of types is whatever is registered in `BanRuleMatcherRegistry`,
 * and no endpoint exposes it — so this is what the UI offers, not what the API
 * accepts. Types seen on existing rules are merged in at render time.
 */
export const KNOWN_RULE_TYPE_OPTIONS: readonly { label: string; value: string }[] = [
    { label: "Project text", value: "project_text" },
];

/** The API caps a rule at 25 terms. */
export const MAX_RULE_TERMS = 25;

export interface BanRuleFormValues {
    type: string;
    name: string;
    description: string;
    terms: string[];
    match_mode: BanRuleMatchMode;
    is_active: boolean;
}

export const banRuleFormSchema = z.object({
    type: z.string().trim().min(1, "Rule type is required"),
    name: z
        .string()
        .trim()
        .min(1, "Rule name is required")
        .max(255, "Keep the name under 255 characters"),
    description: z.string().trim().max(2000, "Keep the description under 2000 characters"),
    terms: z
        .array(z.string().trim().min(1).max(255, "Each term must be 255 characters or less"))
        .min(1, "A ban rule needs at least one term.")
        .max(MAX_RULE_TERMS, `A rule can hold at most ${MAX_RULE_TERMS} terms.`),
    match_mode: z.enum(["word", "substring"]),
    is_active: z.boolean(),
});

export function defaultBanRuleFormValues(): BanRuleFormValues {
    return {
        type: "project_text",
        name: "",
        description: "",
        terms: [],
        match_mode: "word",
        is_active: true,
    };
}

export function ruleToFormValues(rule: BanRule): BanRuleFormValues {
    return {
        type: rule.type,
        name: rule.name,
        description: rule.description ?? "",
        terms: [...rule.terms],
        match_mode: rule.match_mode,
        is_active: rule.is_active,
    };
}

export function formValuesToCreateRulePayload(values: BanRuleFormValues): CreateBanRuleRequest {
    return {
        type: values.type,
        name: values.name.trim(),
        description: values.description.trim() || null,
        terms: normalizeTerms(values.terms),
        match_mode: values.match_mode,
        is_active: values.is_active,
    };
}

/** Sends the full record: replacing `terms` replaces the whole list server-side. */
export function formValuesToUpdateRulePayload(values: BanRuleFormValues): UpdateBanRuleRequest {
    return formValuesToCreateRulePayload(values);
}

/** The API trims and de-duplicates on save; match that so the UI shows the truth. */
export function normalizeTerms(terms: string[]): string[] {
    const seen = new Set<string>();
    const normalized: string[] = [];
    for (const term of terms) {
        const trimmed = term.trim();
        if (!trimmed || seen.has(trimmed)) continue;
        seen.add(trimmed);
        normalized.push(trimmed);
    }
    return normalized;
}

export interface BannedEmailFormValues {
    email: string;
    notes: string;
    /** Empty string means "no rule" — UiSelect models strings. */
    ban_rule_id: string;
}

export const bannedEmailFormSchema = z.object({
    email: z.email("Enter a valid email address").max(255, "Keep the email under 255 characters"),
    notes: z.string().trim().max(2000, "Keep the notes under 2000 characters"),
    ban_rule_id: z.string(),
});

export function defaultBannedEmailFormValues(): BannedEmailFormValues {
    return { email: "", notes: "", ban_rule_id: "" };
}

export function formValuesToBanEmailPayload(values: BannedEmailFormValues): CreateBannedEmailRequest {
    return {
        email: values.email.trim(),
        notes: values.notes.trim() || null,
        ban_rule_id: parseRuleId(values.ban_rule_id),
    };
}

export interface BannedIpFormValues {
    ip_address: string;
    email: string;
    notes: string;
    ban_rule_id: string;
}

export const bannedIpFormSchema = z.object({
    ip_address: z.union([z.ipv4(), z.ipv6()], { error: "Enter a valid IPv4 or IPv6 address" }),
    email: z.union([z.literal(""), z.email("Enter a valid email address").max(255)]),
    notes: z.string().trim().max(2000, "Keep the notes under 2000 characters"),
    ban_rule_id: z.string(),
});

export function defaultBannedIpFormValues(): BannedIpFormValues {
    return { ip_address: "", email: "", notes: "", ban_rule_id: "" };
}

export function formValuesToRecordIpPayload(values: BannedIpFormValues): CreateBannedIpRequest {
    return {
        ip_address: values.ip_address.trim(),
        email: values.email.trim() || null,
        notes: values.notes.trim() || null,
        ban_rule_id: parseRuleId(values.ban_rule_id),
    };
}

function parseRuleId(value: string): number | null {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
}

export interface TestBanRulesFormValues {
    text: string;
    email: string;
    ip: string;
}

export function defaultTestBanRulesFormValues(): TestBanRulesFormValues {
    return { text: "", email: "", ip: "" };
}

export function buildDeleteRuleCopy(rule: BanRule | null) {
    const name = rule?.name?.trim() || "this rule";
    return {
        title: "Delete ban rule?",
        text: `${name} stops screening submissions immediately. Existing bans keep pointing at it. Deactivating the rule instead is the reversible option.`,
        primaryActionLabel: "Delete rule",
        secondaryActionLabel: "Keep rule",
    };
}

export function buildUnbanEmailCopy(ban: BannedEmail | null) {
    const email = ban?.email?.trim() || "this address";
    return {
        title: "Unban this email?",
        text: `${email} will be able to submit again, and the account behind it is restored to regular access.`,
        primaryActionLabel: "Unban email",
        secondaryActionLabel: "Keep ban",
    };
}

export function buildRemoveIpCopy(entry: BannedIp | null) {
    const ip = entry?.ip_address?.trim() || "this address";
    return {
        title: "Remove IP from the registry?",
        text: `${ip} drops off the export list. The API never blocked on this list, so nothing else changes.`,
        primaryActionLabel: "Remove address",
        secondaryActionLabel: "Keep address",
    };
}
